// Hilfsfunktionen für synchronisierte An-/Abreise-Datumsfelder.
// Erwartetes Format: ISO `YYYY-MM-DD` (leerer String = unbesetzt).

const MS_PER_DAY = 86_400_000;

function parseISO(value: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toISO(dt: Date): string {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(value: string, days: number): string {
  const dt = parseISO(value);
  if (!dt) return value;
  dt.setUTCDate(dt.getUTCDate() + days);
  return toISO(dt);
}

function diffDays(from: string, to: string): number | null {
  const a = parseISO(from);
  const b = parseISO(to);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
}

export type DateRange = { from: string; to: string };

export type SyncOptions = {
  /** Mindestnächte zwischen from und to. 0 erlaubt gleiches Datum. */
  minNights?: number;
  /** Bei from-Änderung Dauer (Nächte) beibehalten, sonst nur Mindestabstand sichern. */
  keepDuration?: boolean;
};

/**
 * Synchronisiert ein Datums-Paar nach Änderung an einem der beiden Felder.
 * - Bei Änderung von `from`: optional Dauer mitnehmen; sonst nur sicherstellen,
 *   dass `to >= from + minNights`.
 * - Bei Änderung von `to`: wenn `to < from + minNights`, `from` zurückziehen.
 */
export function syncDateRange(
  prev: DateRange,
  field: "from" | "to",
  nextValue: string,
  opts: SyncOptions = {},
): DateRange {
  const minNights = opts.minNights ?? 1;
  const keepDuration = opts.keepDuration ?? true;

  if (field === "from") {
    if (!nextValue) return { from: "", to: prev.to };
    if (!prev.to) {
      return { from: nextValue, to: addDays(nextValue, minNights) };
    }
    const oldDur = diffDays(prev.from, prev.to);
    if (keepDuration && oldDur != null && oldDur >= minNights) {
      return { from: nextValue, to: addDays(nextValue, oldDur) };
    }
    const minTo = addDays(nextValue, minNights);
    const cmp = diffDays(nextValue, prev.to);
    if (cmp == null || cmp < minNights) {
      return { from: nextValue, to: minTo };
    }
    return { from: nextValue, to: prev.to };
  }

  // field === "to"
  if (!nextValue) return { from: prev.from, to: "" };
  if (!prev.from) {
    return { from: addDays(nextValue, -minNights), to: nextValue };
  }
  const cmp = diffDays(prev.from, nextValue);
  if (cmp == null || cmp < minNights) {
    return { from: addDays(nextValue, -minNights), to: nextValue };
  }
  return { from: prev.from, to: nextValue };
}
